import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { ADMIN_ROOT, findSection, findSetting, sectionPath, settingPath, subTabsAt } from './adminRegistry';
import { AdminLink, useAdminRoute } from './adminRoute';

interface Crumb { label: string; to?: string }

/**
 * The Admin Panel's breadcrumb, rendered in App.tsx's topbar. Every level but
 * the last is a link (each is a real page — see AdminModule.tsx, and a
 * setting's tabs are levels too); the last is
 * the current page, marked aria-current. `compact` (phone) shows only a back
 * link to the parent, which is the pattern a phone expects, beside the
 * current page's name.
 */
export default function AdminBreadcrumbs({ compact = false }: { compact?: boolean }) {
  const route = useAdminRoute();
  const section = findSection(route.section ?? undefined);
  const setting = findSetting(route.setting ?? undefined);

  const crumbs: Crumb[] = [{ label: 'Admin', to: ADMIN_ROOT }];
  if (route.section) crumbs.push({ label: section?.label ?? route.section, to: section ? sectionPath(section.id) : undefined });
  if (route.setting) {
    crumbs.push({ label: setting?.label ?? route.setting, to: setting ? settingPath(setting) : undefined });
    // The setting's tabs (ADMIN_SUBTABS), each level a link to itself.
    if (setting) {
      for (let i = 0; i < route.rest.length; i++) {
        const parents = route.rest.slice(0, i);
        const tab = subTabsAt(setting.id, parents).find((t) => t.id === route.rest[i]);
        if (!tab) break;
        crumbs.push({ label: tab.label, to: `${settingPath(setting)}/${route.rest.slice(0, i + 1).join('/')}` });
      }
    }
  }
  const last = crumbs[crumbs.length - 1];
  if (last) last.to = undefined;

  if (compact) {
    const parent = crumbs.length > 1 ? crumbs[crumbs.length - 2] : undefined;
    return (
      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1 text-[15px]">
        {parent?.to && (
          <AdminLink
            to={parent.to}
            aria-label={`Back to ${parent.label}`}
            className="-ml-1 flex shrink-0 items-center rounded-md px-1 py-1 text-[13px] text-ink-muted no-underline hover:bg-muted hover:text-ink"
          >
            <ChevronLeft aria-hidden className="h-4 w-4" />
            <span className="max-w-[90px] truncate">{parent.label}</span>
          </AdminLink>
        )}
        <span aria-current="page" className="truncate font-semibold text-ink">{last?.label}</span>
      </nav>
    );
  }

  // Five levels (Admin › Communication › Messaging › SMS › Connection) don't
  // fit beside the hospital name and search box, and truncating every crumb
  // left nothing readable. The section is the crumb to give up — the rail
  // heading says it too — so it collapses to "…" (still a link, still named).
  const collapseSection = crumbs.length > 4;

  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="m-0 flex min-w-0 list-none items-center gap-0.5 p-0 text-[14px]">
        {crumbs.map((c, i) => {
          const isLast = i === crumbs.length - 1;
          const collapsed = collapseSection && i === 1;
          return (
            <li key={`${i}-${c.label}`} className={cn('flex items-center gap-0.5', isLast ? 'min-w-0' : 'shrink-0')}>
              {i > 0 && <ChevronRight aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
              {c.to ? (
                <AdminLink
                  to={c.to}
                  title={collapsed ? c.label : undefined}
                  aria-label={collapsed ? c.label : undefined}
                  className="max-w-[180px] truncate rounded-md px-1.5 py-1 text-ink-muted no-underline transition-colors hover:bg-muted hover:text-ink hover:underline underline-offset-[3px]"
                >
                  {collapsed ? '…' : c.label}
                </AdminLink>
              ) : (
                <span aria-current="page" className="truncate px-1.5 py-1 font-semibold text-ink">{c.label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
